import {localStorageKeysConstants} from '../../constants/localStorage.constants';
import {REPORT_EXPORTS_FIELD, REPORT_HTML_FIELD, REPORT_ID_FIELD, REPORT_PARAMS_FIELD} from '../../constants/agentChat.constants';

const {FULL_WIDTH_AGENT_CHAT_SESSION} = localStorageKeysConstants;

const ROLES = ['user', 'assistant'];

/**
 * The full-width chat a page reload can resume: its session id, remembered per company.
 *
 * ⚠ ONLY THE ID IS KEPT IN THE BROWSER, NEVER THE MESSAGES. The history endpoint answers only the caller's own sessions
 * (a 404 for anyone else's), so a remembered id cannot open another user's chat, whereas stored messages would put one
 * person's figures in front of whoever signs in next. Logout clears the key with every other one (`clearUserStoredData`).
 */
export const agentChatHistoryHelpers = {
  rememberSession: ({companyId, sessionId}) => {
    if (!companyId || !sessionId) return;
    try {
      localStorage.setItem(FULL_WIDTH_AGENT_CHAT_SESSION, JSON.stringify({companyId, sessionId}));
    } catch (error) {
      // Storage unavailable (private mode, quota): the chat simply can't be resumed after a reload.
    }
  },

  /** The session remembered for this company, or null. */
  getRememberedSession: (companyId) => {
    try {
      const stored = JSON.parse(localStorage.getItem(FULL_WIDTH_AGENT_CHAT_SESSION) || 'null');
      return stored?.companyId === companyId && typeof stored?.sessionId === 'string' ? stored.sessionId : null;
    } catch (error) {
      return null;
    }
  },

  forgetSession: () => {
    try {
      localStorage.removeItem(FULL_WIDTH_AGENT_CHAT_SESSION);
    } catch (error) {
      // Nothing to forget when storage is unavailable.
    }
  },

  /**
   * A session's stored messages as the chat draws them, oldest first.
   *
   * ⚠ BUILT AGAINST THE HISTORY FORMAT PROPOSED ON WIT-220, which the endpoint does not return yet. Each item carries
   * `role` and `message`, and a report adds `content_type`, `report_html` and `exports`: the live answer's fields,
   * mapped the way `useAgentChat` maps a live answer. An item without `role` and `message`, which is all today's raw
   * `message_data` is, is skipped, so a history with nothing drawable gives [] and nothing is offered for resuming.
   */
  toChatMessages: (results) => (Array.isArray(results) ? results : [])
    .filter((item) => ROLES.includes(item?.role) && typeof item?.message === 'string')
    // Parsed, not compared as text: a string sort is chronological only while every `created_at` is
    // written exactly alike, and that format is what WIT-260 has still to settle. One `+02:00` offset
    // or one different precision among them and the conversation quietly reorders itself. An absent or
    // unparseable date sorts first instead of poisoning the comparison with NaN.
    .sort((a, b) => (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0))
    .map((item, index) => ({
      id: item.id || `history-${index}`,
      role: item.role,
      type: 'text',
      message: item.message,
      contentType: item.content_type,
      reportHtml: item[REPORT_HTML_FIELD],
      exports: item[REPORT_EXPORTS_FIELD],
      reportId: item[REPORT_ID_FIELD],
      reportParams: item[REPORT_PARAMS_FIELD],
    })),
};
